import React, { useContext, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import css from "../style.module.css"
import { AdminPanelContext } from '../../../Context/AdminPanelContext';

export default function AdminPanelForm({ action, user = {}, onClose }) {
    const { updateMarketingUsers, addMarketingUsers, error } = useContext(AdminPanelContext);

    const isUpdate = action === 'update';
    const [loading, setLoading] = useState(false);

    const getActiveValue = (active) => {
        if (active === true || active === "Active" || active === "true") {
            return 'true';
        } else if (active === false || active === "Inactive" || active === "false") {
            return 'false';
        }
        return 'true';
    };

    const initialValues = {
        name: user.name || '',
        email: user.email || '',
        userRole: user.role || 'baseUser',
        active: getActiveValue(user.active),
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        email: isUpdate
            ? Yup.string().email('Invalid email format')
            : Yup.string()
                .email('Invalid email format')
                .required('Email is required'),
        userRole: Yup.string()
            .oneOf(['admin', 'marketing', 'compliance', 'baseUser'], 'Invalid role')
            .required('Role is required'),
        active: Yup.string()
            .oneOf(['true', 'false'], 'Status is required')
            .required('Status is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setLoading(true);

        const addPayload = {
            name: values.name,
            userRole: values.userRole,
            active: values.active === 'true',
            email: values.email
        };

        const updatePayload = {
            id: user.id,
            name: values.name,
            userRole: values.userRole,
            active: values.active === 'true',
        };

        try {
            if (isUpdate) {
                const { success } = await updateMarketingUsers(updatePayload);
                if (success) {
                    onClose();
                }
            } else {
                const { success } = await addMarketingUsers(addPayload);
                if (success) {
                    onClose();
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <div className={css.adminLayout}>
            <div className={css.formContainer}>
                <button className={`btn ${css.closeButton}`} onClick={onClose}>
                    ×
                </button>
                <h2>{isUpdate ? 'Update Internal User' : 'Add Internal User'}</h2>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ isSubmitting, errors, touched }) => {
                        return (
                            <Form noValidate>
                                {/* Name */}
                                <div className="form-group mt-2">
                                    <label htmlFor="name">Name</label>
                                    <Field
                                        name="name"
                                        type="text"
                                        className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                                    />
                                    <ErrorMessage name="name" component="div" className="invalid-feedback" />
                                </div>

                                {/* Email (only required when adding) */}
                                {!isUpdate && (
                                    <div className="form-group mt-2">
                                        <label htmlFor="email">Email</label>
                                        <Field
                                            name="email"
                                            type="email"
                                            className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                                        />
                                        <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                    </div>
                                )}

                                <div className="form-group mt-3">
                                    <label htmlFor="userRole">Portal Role</label>
                                    <Field
                                        as="select"
                                        name="userRole"
                                        className={`form-control ${errors.userRole && touched.userRole ? 'is-invalid' : ''}`}
                                    >
                                        <option value="baseUser">Base User</option>
                                        <option value="compliance">Compliance</option>
                                        <option value="marketing">Marketing</option>
                                        <option value="admin">Admin</option>
                                    </Field>
                                    <ErrorMessage name="userRole" component="div" className="invalid-feedback" />
                                    <small className="text-muted d-block mt-1">
                                        Admins can manage internal users. Marketing and compliance users can access their assigned portal workflows. Base users have standard portal access.
                                    </small>
                                </div>

                                {/* Active / Inactive */}
                                <div className="form-group mt-2">
                                    <label htmlFor="active">Status</label>
                                    <Field
                                        as="select"
                                        name="active"
                                        className={`form-control ${errors.active && touched.active ? 'is-invalid' : ''}`}
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </Field>
                                    <ErrorMessage name="active" component="div" className="invalid-feedback" />
                                </div>

                                {/* Submit */}
                                <button
                                    type="submit"
                                    className="btn btn-primary mt-3"
                                    disabled={isSubmitting || loading}
                                >
                                    {isUpdate ? 'Update User' : 'Create User'}
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                    )}
                                </button>
                                {error && <div className="text-danger mt-2">{error}</div>}
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
}
